import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Image } from 'react-native';
import { Invitation } from '@/types/residents/home';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { GlassCard } from '../GlassCard';
import { getDisplayName, getInitials } from '@/utils/residents/utils';
import { hasPassedHours } from '@/utils/residents/date';
import { invitationsStrings } from '@/constants/residents/strings';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { getAvatarColorById } from '@/utils/residents/avatarColors';

interface InvitationCardProps {
  invitation: Invitation;
  onAccept?: (invitation: Invitation) => void;
  onDecline?: (invitation: Invitation) => void;
  onCancel?: (invitation: Invitation) => void;
  onResend?: (invitation: Invitation) => void;
}

export const InvitationCard: React.FC<InvitationCardProps> = React.memo(({
  invitation,
  onAccept,
  onDecline,
  onCancel,
  onResend,
}) => {
  const { colors, mode } = useThemeColors();
  const { user, sender, createdAt } = invitation;
  const displayName = getDisplayName(user.firstName, user.lastName);
  const nameText = displayName || user.email;
  const initials = useMemo(() => getInitials(user.firstName, user.lastName), [user.firstName, user.lastName]);
  const avatarColor = useMemo(() => getAvatarColorById(user.id, mode), [user.id, mode]);
  const isResendActive = useMemo(() => hasPassedHours(createdAt, 24), [createdAt]);

  const subtitle = sender
    ? invitationsStrings.cell.didInvite
    : 'Has invited you to connect';

  return (
    <GlassCard borderRadius={16}>
      {/* Header: Avatar + Name/Subtitle + Badge */}
      <View style={styles.header}>
        <View style={[styles.avatar, { backgroundColor: avatarColor.bg }]}>
          {user.photoUrl ? (
            <Image source={{ uri: user.photoUrl }} style={styles.avatarImage} />
          ) : initials ? (
            <Text style={[styles.avatarInitials, { color: avatarColor.text }]}>{initials}</Text>
          ) : (
            <RESIDENTS_SVG_COMPONENTS.USER width={20} height={20} color={avatarColor.text} />
          )}
        </View>

        <View style={styles.nameContainer}>
          <Text style={[styles.name, { color: colors.text.tertiary }]} numberOfLines={1}>{nameText}</Text>
          <Text style={[styles.subtitle, { color: colors.text.dim }]} numberOfLines={1}>{subtitle}</Text>
        </View>

        {!sender && (
          <View style={[styles.newBadge, { backgroundColor: colors.accent.blueBg }]}>
            <Text style={styles.newBadgeText}>New</Text>
          </View>
        )}
      </View>

      {/* Action Buttons */}
      <View style={[styles.actionsContainer, { borderTopColor: colors.separator }]}>
        {sender ? (
          <>
            <TouchableOpacity
              style={[
                styles.actionButton,
                { backgroundColor: isResendActive ? colors.accent.blueBgMedium : colors.glass.buttonBg },
                !isResendActive && styles.buttonDisabled,
              ]}
              onPress={() => isResendActive && onResend?.(invitation)}
              activeOpacity={isResendActive ? 0.7 : 1}
              disabled={!isResendActive}
            >
              <RESIDENTS_SVG_COMPONENTS.REFRESH
                width={14}
                height={14}
                color={isResendActive ? PRIMITIVE_COLORS.resioBlue : colors.text.dim}
              />
              <Text style={[styles.acceptText, !isResendActive && { color: colors.text.dim }]}>
                {invitationsStrings.cell.resend}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.actionButton, { backgroundColor: colors.glass.buttonBg }]}
              onPress={() => onCancel?.(invitation)}
              activeOpacity={0.7}
            >
              <Text style={[styles.declineText, { color: colors.text.dim }]}>{invitationsStrings.cell.cancelTitle}</Text>
            </TouchableOpacity>
          </>
        ) : (
          <>
            <TouchableOpacity
              style={[styles.actionButton, { backgroundColor: colors.accent.blueBgMedium }]}
              onPress={() => onAccept?.(invitation)}
              activeOpacity={0.7}
            >
              <Text style={styles.acceptText}>Accept</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.actionButton, { backgroundColor: colors.glass.buttonBg }]}
              onPress={() => onDecline?.(invitation)}
              activeOpacity={0.7}
            >
              <Text style={[styles.declineText, { color: colors.text.dim }]}>Decline</Text>
            </TouchableOpacity>
          </>
        )}
      </View>
    </GlassCard>
  );
});

InvitationCard.displayName = 'InvitationCard';

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: 44,
    height: 44,
    borderRadius: 22,
  },
  avatarInitials: {
    fontSize: 16,
    fontWeight: '700',
  },
  nameContainer: {
    flex: 1,
    gap: 2,
  },
  name: {
    fontSize: 15,
    fontWeight: '600',
  },
  subtitle: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  newBadge: {
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  newBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: PRIMITIVE_COLORS.resioBlue,
  },
  actionsContainer: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 16,
    paddingTop: 17,
    borderTopWidth: 1,
  },
  actionButton: {
    flex: 1,
    height: REDESIGN_SPACING.buttonHeightSmall,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 6,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  acceptText: {
    fontSize: 13,
    fontWeight: '600',
    color: PRIMITIVE_COLORS.resioBlue,
    textAlign: 'center',
  },
  declineText: {
    fontSize: 13,
    fontWeight: '600',
    textAlign: 'center',
  },
});
